import type { CanvasLocalFilePreview } from "../context/CanvasLocalFilePreviewContext";

export type FileRecordDisplayFields = {
  id?: string;
  title?: string;
  status?: string;
  url?: string;
  content_type?: string;
  preview_urls?: Record<string, string> | null;
};

export type MergeLocalPreviewOptions = {
  /** When false, keep the blob visible for video uploads even after `processed`. */
  videoHandoffReady?: boolean;
};

export function fileRecordIsProcessed(status: string | undefined): boolean {
  return status === "processed";
}

export function fileRecordHasDisplayableContent(
  data: FileRecordDisplayFields | null | undefined,
): boolean {
  if (!data) return false;
  if (data.url && data.url.length > 0) return true;
  if (!data.preview_urls) return false;
  return Object.values(data.preview_urls).some((u) => u.length > 0);
}

function shouldKeepLocalPreview(
  data: FileRecordDisplayFields,
  localPreview: CanvasLocalFilePreview,
  options?: MergeLocalPreviewOptions,
): boolean {
  if (!fileRecordIsProcessed(data.status)) return true;
  if (!localPreview.contentType.startsWith("video/")) return false;
  return options?.videoHandoffReady !== true;
}

export function mergeFileRecordWithLocalPreview(
  data: FileRecordDisplayFields | null | undefined,
  localPreview: CanvasLocalFilePreview | undefined,
  options?: MergeLocalPreviewOptions,
): FileRecordDisplayFields | null | undefined {
  if (!data || !localPreview) return data;
  if (shouldKeepLocalPreview(data, localPreview, options)) {
    return {
      ...data,
      url: localPreview.url,
      content_type: localPreview.contentType,
    };
  }
  if (fileRecordHasDisplayableContent(data)) return data;
  return {
    ...data,
    url: localPreview.url,
    content_type: localPreview.contentType,
  };
}

/** Only drop the in-memory blob once the server row is `processed` with media. */
export function shouldRevokeLocalFilePreview(
  data: FileRecordDisplayFields | null | undefined,
  localPreview?: CanvasLocalFilePreview,
): boolean {
  if (!data || !fileRecordIsProcessed(data.status)) return false;
  if (localPreview?.contentType.startsWith("video/")) return false;
  return fileRecordHasDisplayableContent(data);
}

export function shouldShowFileRecordSkeleton(params: {
  loading?: boolean;
  isFileRecordEntity: boolean;
  fileRecordData: FileRecordDisplayFields | null | undefined;
  localPreview: CanvasLocalFilePreview | undefined;
  videoHandoffReady?: boolean;
}): boolean {
  const { loading, isFileRecordEntity, fileRecordData, localPreview } = params;
  if (!isFileRecordEntity && !loading) return false;

  const displayData = mergeFileRecordWithLocalPreview(
    fileRecordData,
    localPreview,
    { videoHandoffReady: params.videoHandoffReady },
  );
  if (fileRecordHasDisplayableContent(displayData)) return false;

  return loading === true || isFileRecordEntity;
}
